import React, {useMemo, useState, useCallback} from 'react';
import {useDispatch} from 'react-redux';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import {actionIcDelete24px, contentIcCreate24px} from '@ey/motif-react-wrapper/assets/icons/index';
import {labels} from '../../../../../util/utils';
import {
	relateRiskFactorToDescription,
	unRelateRiskFactorFromDescription,
	editRiskFactorDescription,
	findRiskFactorDescription,
	dropdownActions
} from '../../../../../actions/RiskFactor/riskfactoractions';
import ReactSelect from '../../../SelectDropdown/SelectDropdown';
import EllipsesControl from '../../../EllipsesControl/EllipsesControl';
import RiskFactorDescriptionModal from './RiskFactorDescriptionModal';
import {StyledLinkLabel} from '../../../StyledElements/StyledElements';
import DisablingSection from '../../..//DisablingSection/DisablingSection';
import Icon from '../../../BaseComponentWrappers/MotifIcon';
import InlineMessage from '../../../BaseComponentWrappers/MotifInlineMessage';
import _ from 'lodash';

function RiskFactorDescriptionDropdown({slice, options, engagementId, riskFactorId, riskFactorRelationId, onSave, isDisabled}) {
	const option = useMemo(() => {
		let desc = riskFactorRelationId ? findRiskFactorDescription(slice, riskFactorId) : undefined;
		return options.find((o) => o.label === desc) || {};
	}, [slice]);

	const hasOption = !_.isEmpty(option);

	const [showModal, setShowModal] = useState(false);
	const [mode, setMode] = useState();
	const [showOptionButtons, setShowOptionButtons] = useState(false);
	const [onDropdownFocus, setOnDropdownFocus] = useState(false);

	const dispatch = useDispatch();

	const onDescriptionSetSelect = (description) => {
		return onSave(description.label).then(() => {
			dispatch(relateRiskFactorToDescription(riskFactorId, description.label));
			setShowOptionButtons(false);
		});
	};

	const dropdownFormLabel = (label) => {
		return <EllipsesControl content={label} tooltip={label} noOfLines={1} />;
	};
	const handleOnHide = () => {
		setShowModal(false);
	};

	const openModal = (mode) => {
		setMode(mode);
		setShowModal(true);
	};

	const manageDescriptionStore = useCallback(
		(description, oldDescription) => {
			switch (mode) {
				case dropdownActions.add:
					return dispatch(relateRiskFactorToDescription(riskFactorId, description, oldDescription));
				case dropdownActions.edit:
					return dispatch(editRiskFactorDescription(riskFactorId, description, oldDescription));
			}
		},
		[mode, engagementId, riskFactorId]
	);

	const modalValue = useMemo(() => {
		return mode === dropdownActions.edit ? option?.label : '';
	}, [mode, option]);

	const handleOnSave = (desc) => {
		return onSave(desc, mode).then(() => {
			manageDescriptionStore(desc, option?.label);
			setShowOptionButtons(false);
			setOnDropdownFocus(false);
		});
	};

	const removeRiskFactorDescription = () => {
		return onSave(null, dropdownActions.remove).then(() => {
			dispatch(unRelateRiskFactorFromDescription(riskFactorId, option?.label));
			setShowOptionButtons(false);
			setOnDropdownFocus(false);
		});
	};

	return (
		<StyledDescriptionDropdown
			className="StyldDedDescriptionDropdown"
			onMouseEnter={() => {
				if (hasOption) {
					setShowOptionButtons(true);
				}
			}}
			onMouseLeave={() => {
				setShowOptionButtons(false);
			}}
		>
			<>
				{hasOption ? (
					<StyledOptionLabel className={'StyledOptionLabel'}>
						<EllipsesControl content={option.label} title={option.label} className="Label">
							{option.label}
						</EllipsesControl>
					</StyledOptionLabel>
				) : (
					<ReactSelectWrapper className="ReactSelectWrapper">
						<ReactSelect
							options={options}
							value={hasOption ? option : {value: -1, label: labels.riskFactorNoDescription}}
							defaultValue={option}
							formLabel={dropdownFormLabel(labels.riskFactorDescription)}
							onChange={onDescriptionSetSelect}
							openMenuOnFocus={onDropdownFocus}
							onMenuOpen={() => {
								setOnDropdownFocus(true);
							}}
							formatOptionLabel={({label}) => {
								return (
									<EllipsesControl
										tooltipClass="RiskFactorDescriptionTooltipEllipsis"
										content={label}
										tooltip={label}
										noOfLines={1}
									/>
								);
							}}
							footer={
								<>
									{!isDisabled && (
										<StyledAddDescriptionLabel
											className="StyledAddDescriptionLabel"
											onClick={() => openModal(dropdownActions.add)}
										>
											<EllipsesControl content={labels.addNewDescription} noOfLines={1} />
										</StyledAddDescriptionLabel>
									)}
								</>
							}
							noOptionsMessage={() => labels.noResultsFound}
							className={'RelatedDescriptionDropdown'}
							isDisabled={isDisabled}
						/>
						{!hasOption && (
							<InlineMessage
								variant="warning"
								className={'inline-message'}
								children={<span className="error-message">{labels.riskFactorEmptyWarning}</span>}
							/>
						)}
					</ReactSelectWrapper>
				)}
			</>

			{showOptionButtons && hasOption && (
				<StyledDropdownActions className="StyledDropdownActions">
					<DisablingSection disabled={!hasOption || isDisabled}>
						<StyledLinkLabel className="Edit LinkWrapper" onClick={() => openModal(dropdownActions.edit)}>
							<Icon src={contentIcCreate24px} />
							<span className="LinkLabel">
								<EllipsesControl content={labels.edit} tooltip={labels.edit} noOfLines={1} />
							</span>
						</StyledLinkLabel>
						<StyledLinkLabel className="Remove LinkWrapper" onClick={removeRiskFactorDescription}>
							<Icon src={actionIcDelete24px} />
							<span className="LinkLabel">
								<EllipsesControl content={labels.remove} tooltip={labels.remove} noOfLines={1} />
							</span>
						</StyledLinkLabel>
					</DisablingSection>
				</StyledDropdownActions>
			)}
			{showModal && (
				<RiskFactorDescriptionModal onSave={handleOnSave} onHide={handleOnHide} value={modalValue} mode={mode} />
			)}
		</StyledDescriptionDropdown>
	);
}

export default RiskFactorDescriptionDropdown;

RiskFactorDescriptionDropdown.propTypes = {
	options: PropTypes.arrayOf(
		PropTypes.shape({
			value: PropTypes.oneOf([PropTypes.string, PropTypes.number]),
			label: PropTypes.string
		})
	),
	engagementId: PropTypes.number.isRequired,
	initialDescription: PropTypes.string,
	onSave: PropTypes.func.isRequired
};

const ReactSelectWrapper = styled.section`
	display: flex;
	flex-direction: column;
	.motif-inline-message {
		margin-top: var(--px-10);
		align-items: center;
		.motif-inline-message-content {
			.motif-icon {
				svg {
					width: var(--px-20) !important;
					height: var(--px-20) !important;
					margin-right: var(--px-5);
					fill: var(--orange-500);
				}
			}
		}
	}
`;

const StyledDescriptionDropdown = styled.section`
	display: flex;
	align-items: center;
	width: var(--px-900);
	.RelatedDescriptionDropdown {
		width: var(--px-680);
		.react-select-label {
			max-width: var(--px-550);
		}
		.react-select-container {
			.react-select__value-container {
				.react-select__single-value {
					line-height: normal;
				}
			}
			.react-select__menu {
				.react-select__menu-list {
					max-height: var(--px-200);
				}
			}
		}
	}
`;

const StyledDropdownActions = styled.section`
	display: flex;
	align-items: center;
	width: var(--px-200);
	margin-left: var(--px-10);
	.LinkWrapper {
		display: flex;
		align-items: center;
		margin: 0 var(--px-10) 0 0;
		width: auto;
		max-width: var(--px-100);
		&:hover {
			text-decoration: underline;
		}
		.motif-icon {
			width: var(--px-20) !important;
			height: var(--px-20) !important;
			svg {
				width: var(--px-20) !important;
				height: var(--px-20) !important;
				color: var(--blue-600) !important;
			}
		}
		.LinkLabel {
			padding-left: var(--px-8);
			min-height: var(--px-20);
			display: inline-flex;
			align-items: center;
			font-size: var(--px-14);
			font-weight: 400;
			line-height: var(--px-20);
			width: auto;
			max-width: var(--px-75);
		}
	}
`;

const StyledAddDescriptionLabel = styled(StyledLinkLabel)`
	width: 100%;
	display: block;
	margin: 0;
	padding: var(--px-12) var(--px-14);
	border-top: var(--px-1) solid var(--neutrals-100);
	color: var(--blue-600) !important;
	font-weight: 400;
	font-size: var(--px-14);
	line-height: var(--px-20);
	background-color: var(--neutrals-00white);
`;

const StyledOptionLabel = styled.section`
	display: block;
	width: var(--px-600);
	overflow: hidden;
	white-space: nowrap;
	text-overflow: ellipsis;
	.NotaTooltipSection {
		font-size: var(--px-14);
		font-weight: 300;
		line-height: var(--px-20);
		color: var(--neutrals-700);
	}
`;
